/**
 * Theme registry. Adding a theme means adding one entry here, one matching
 * `[data-theme="<id>"]` block in `src/assets/styles/themes.css`, and — if it
 * needs a font family not already loaded — a Google Fonts family in
 * index.html. `preview` values are hand-copied from that CSS block purely
 * for the swatches in ThemeSelector.vue; they don't drive the actual theme.
 */

export type ThemeId =
  | 'light'
  | 'dark'
  | 'midnight'
  | 'sepia'
  | 'ocean'
  | 'contrast'
  | 'shibuya'
  | 'outrun'
  | 'cocoa'
  | 'aubergine'
  | 'circuit'
  | 'chai'
  | 'codefall'
  | 'nightshade'
  | 'ember'
  | 'macaron'
  | 'ion'
  | 'voltage'
  | 'rosewood'
  | 'forge'
  | 'classic'
  | 'classic-dark'

export interface ThemePreview {
  surface: string
  card: string
  navy: string
  accent: string
}

export interface ThemeDefinition {
  id: ThemeId
  name: string
  description: string
  /** Whether this theme should carry Tailwind's `dark:` variant (darkMode: 'class'). */
  isDark: boolean
  preview: ThemePreview
}

export const THEMES: ThemeDefinition[] = [
  {
    id: 'light',
    name: 'Turtledove',
    description: 'Default theme, matching the Turtledove Cyber brand.',
    isDark: false,
    preview: { surface: '#F7FAF3', card: '#FFFFFF', navy: '#092422', accent: '#D2FF71' },
  },
  {
    id: 'dark',
    name: 'Turtledove Dark',
    description: 'Low-light variant of the Turtledove theme: deep green and lime.',
    isDark: true,
    preview: { surface: '#092422', card: '#132D30', navy: '#051A18', accent: '#D2FF71' },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Deep indigo with a cyan accent and geometric headings.',
    isDark: true,
    preview: { surface: '#020617', card: '#0F172A', navy: '#1E1B4B', accent: '#22D3EE' },
  },
  {
    id: 'sepia',
    name: 'Sepia',
    description: 'Warm, paper-toned theme with serif headings.',
    isDark: false,
    preview: { surface: '#FBF3E7', card: '#FFFBF3', navy: '#7C4A2D', accent: '#C9822B' },
  },
  {
    id: 'ocean',
    name: 'Ocean',
    description: 'Cool teal and blue, light and airy.',
    isDark: false,
    preview: { surface: '#F0FAFB', card: '#FFFFFF', navy: '#0E4F5C', accent: '#14B8A6' },
  },
  {
    id: 'contrast',
    name: 'Contrast',
    description: 'High-contrast black and white with a bold amber accent, for maximum legibility.',
    isDark: true,
    preview: { surface: '#000000', card: '#171717', navy: '#000000', accent: '#FBBF24' },
  },
  {
    id: 'shibuya',
    name: 'Shibuya',
    description: 'Cool night-city blues and purples for late-night sessions.',
    isDark: true,
    preview: { surface: '#1A1B26', card: '#1F2335', navy: '#3B4261', accent: '#7AA2F7' },
  },
  {
    id: 'outrun',
    name: 'Outrun',
    description: 'Neon pink and cyan on deep violet - 1980s retro-futurism.',
    isDark: true,
    preview: { surface: '#241B2F', card: '#2A2139', navy: '#4C1D95', accent: '#F92AAD' },
  },
  {
    id: 'cocoa',
    name: 'Cocoa',
    description: 'Warm dark olive-brown with punchy pink, green and gold, and monospaced headings.',
    isDark: true,
    preview: { surface: '#272822', card: '#2D2E26', navy: '#3E3D32', accent: '#A6E22E' },
  },
  {
    id: 'aubergine',
    name: 'Aubergine',
    description: 'Clean white workspace with a bold aubergine-purple brand accent.',
    isDark: false,
    preview: { surface: '#F8F8F8', card: '#FFFFFF', navy: '#4A154B', accent: '#2BAC76' },
  },
  {
    id: 'circuit',
    name: 'Circuit',
    description: 'Crisp off-white with a PCB teal-and-copper palette, monospaced headings.',
    isDark: false,
    preview: { surface: '#F5F7F7', card: '#FFFFFF', navy: '#00979D', accent: '#E76F51' },
  },
  {
    id: 'chai',
    name: 'Chai',
    description: 'Milky, spiced-tea warmth with cinnamon and cardamom accents.',
    isDark: false,
    preview: { surface: '#FBF6EE', card: '#FFFDF8', navy: '#6F4E37', accent: '#D97742' },
  },
  {
    id: 'codefall',
    name: 'Codefall',
    description: 'Phosphor-green digital rain on black, full monospace commitment.',
    isDark: true,
    preview: { surface: '#000000', card: '#001100', navy: '#003B00', accent: '#00FF41' },
  },
  {
    id: 'nightshade',
    name: 'Nightshade',
    description: 'Gothic purple-gray with vivid pink, cyan, green and yellow accents.',
    isDark: true,
    preview: { surface: '#282A36', card: '#44475A', navy: '#44475A', accent: '#FF79C6' },
  },
  {
    id: 'ember',
    name: 'Ember',
    description: 'Warm retro-terminal browns and mustard, with a hot orange accent.',
    isDark: true,
    preview: { surface: '#282828', card: '#3C3836', navy: '#504945', accent: '#FE8019' },
  },
  {
    id: 'macaron',
    name: 'Macaron',
    description: 'Soft pastel lavender and pink on a light, cozy base.',
    isDark: false,
    preview: { surface: '#EFF1F5', card: '#FFFFFF', navy: '#8839EF', accent: '#EA76CB' },
  },
  {
    id: 'ion',
    name: 'Ion',
    description: 'The familiar professional coder default - balanced slate-blue and cyan.',
    isDark: true,
    preview: { surface: '#282C34', card: '#2C313A', navy: '#61AFEF', accent: '#56B6C2' },
  },
  {
    id: 'voltage',
    name: 'Voltage',
    description: 'Aggressive neon - acid yellow and cyan on near-black.',
    isDark: true,
    preview: { surface: '#0A0A0F', card: '#16121A', navy: '#141414', accent: '#FCEE0A' },
  },
  {
    id: 'rosewood',
    name: 'Rosewood',
    description: 'Moody dusky rose, pine and gold - designed for aesthetics.',
    isDark: true,
    preview: { surface: '#191724', card: '#1F1D2E', navy: '#31748F', accent: '#EBBCBA' },
  },
  {
    id: 'forge',
    name: 'Forge',
    description: 'The clean, familiar workshop look everyone already knows.',
    isDark: false,
    preview: { surface: '#F6F8FA', card: '#FFFFFF', navy: '#0969DA', accent: '#1A7F37' },
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'The original NEST navy-and-green look.',
    isDark: false,
    preview: { surface: '#F9FAFB', card: '#FFFFFF', navy: '#0B3F5F', accent: '#97CB50' },
  },
  {
    id: 'classic-dark',
    name: 'Classic Dark',
    description: 'The original NEST dark mode.',
    isDark: true,
    preview: { surface: '#111827', card: '#1F2937', navy: '#0B3F5F', accent: '#97CB50' },
  },
]

export const DEFAULT_THEME: ThemeId = 'light'

export function isThemeId(value: string | null | undefined): value is ThemeId {
  return !!value && THEMES.some(theme => theme.id === value)
}

export function getTheme(id: ThemeId): ThemeDefinition {
  return THEMES.find(theme => theme.id === id) ?? THEMES[0]
}
